import { catchError, map, Observable, of } from 'rxjs'
import { HttpResponse } from '@angular/common/http'
import { Injectable } from '@angular/core'

import { ApiClass } from '@data/class/api-class'
import { environment } from '@env/environment'

// ── Tipos del reporte (espejo del backend api-rendafin/src/modules/invoices/dto) ──
export type ImportRowStatus = 'CREATED' | 'UPDATED' | 'ERROR'
export interface ImportInvoiceReportRow {
  rowNumber: number
  folio: string | null
  folioFiscal: string | null // Serie+Folio del CFDi (ej. A1198), llave de ligado copiada del nombre del archivo
  uid?: string // UUID fiscal resuelto desde el XML (Fase 2). Opcional: null mientras es PENDING_FILES
  tipo: string | null
  mes: string | null
  status: ImportRowStatus
  message: string
  invoiceId?: string
}
export interface ImportInvoiceResult {
  totalRows: number
  created: number
  updated: number
  errors: number
  rows: ImportInvoiceReportRow[]
}
export interface UploadedInvoiceFileResult {
  fileName: string
  folioFiscal: string | null // Serie+Folio derivado del nombre del archivo (llave de ligado)
  uid?: string // UUID fiscal leído del XML emparejado. Opcional: solo presente cuando se resolvió del XML
  kind: 'pdf' | 'xml' | 'unknown' // tipo de ARCHIVO
  docType?: 'complement' // marca las filas ruteadas al flujo de complemento de pago (REP)
  complementId?: string // id del PaymentComplement creado/actualizado (cuando docType='complement')
  status: 'UPLOADED' | 'SKIPPED' | 'ERROR'
  message: string
  invoiceId?: string
}
export interface UploadInvoiceFilesResult {
  totalFiles: number
  uploaded: number
  matchedInvoices: number
  skippedUnmatched: UploadedInvoiceFileResult[]
  errors: UploadedInvoiceFileResult[]
  results: UploadedInvoiceFileResult[]
}

interface Wrapped<T> {
  error: boolean
  msg: string
  data: T
}

@Injectable({ providedIn: 'root' })
export class ApiInvoiceService extends ApiClass {
  constructor() {
    super()
    this.path = 'invoices'
  }

  // ── Dashboard ──
  // app-main-table espera getAll(page, limit, filters) → resuelve a { data: { data, totalItems } }.
  getAll(page = 1, limit = 10, filters = {}): Observable<any> {
    const populate = [{ path: 'amortizationPeriod', select: ['period', 'date'] }]
    return this.http
      .post<any>(`${environment.apiUrl}invoices`, { page, limit, filters, populate })
      .pipe(catchError(() => of({ error: true, msg: 'Error al consultar facturas', data: { data: [], totalItems: 0, page, limit } })))
  }

  getSummary(month?: string): Observable<Wrapped<any>> {
    const url = `${environment.apiUrl}invoices/summary${month ? `?month=${encodeURIComponent(month)}` : ''}`
    return this.http.get<any>(url).pipe(
      map((r) => ({ error: r?.error ?? false, msg: r?.msg ?? '', data: r?.data ?? null })),
      catchError(() => of({ error: true, msg: 'Error al consultar el resumen', data: null })),
    )
  }

  // Folio inicial autocalculado del layout FACTURAS PENDIENTES (máximo folio serie A registrado + 1).
  // source: 'db' (derivado de facturas) | 'seed' (aún no hay facturas → arranca en el valor semilla).
  getNextFolio(): Observable<Wrapped<{ nextFolio: number; source: 'db' | 'seed' } | null>> {
    return this.http.get<any>(`${environment.apiUrl}invoices/pending/next-folio`).pipe(
      map((r) => ({ error: r?.error ?? false, msg: r?.msg ?? '', data: r?.data ?? null })),
      catchError(() => of({ error: true, msg: 'Error al calcular el folio inicial', data: null })),
    )
  }

  // El endpoint usa @Res() (no pasa por el ResponseInterceptor) → blob crudo.
  downloadTemplate(): Observable<HttpResponse<Blob>> {
    return this.http.get(`${environment.apiUrl}invoices/template`, { responseType: 'blob', observe: 'response' })
  }

  // Reporte FACTURAS PENDIENTES: una fila por concepto de cada lease Activo con facturación
  // configurada. startFolio es opcional (el backend lo autocalcula si se omite); month (YYYY-MM)
  // alimenta el default de Comentarios. Usa @Res() en el backend → blob crudo; se devuelve la
  // respuesta completa para leer el filename.
  downloadPendingInvoices(startFolio?: number, month?: string): Observable<HttpResponse<Blob>> {
    const parts: string[] = []
    if (startFolio != null) parts.push(`startFolio=${encodeURIComponent(startFolio)}`)
    if (month) parts.push(`month=${encodeURIComponent(month)}`)
    const query = parts.length ? `?${parts.join('&')}` : ''
    return this.http.get(`${environment.apiUrl}invoices/pending/export${query}`, { responseType: 'blob', observe: 'response' })
  }

  importExcel(file: File, userId?: string): Observable<Wrapped<ImportInvoiceResult | null>> {
    const fd = new FormData()
    fd.append('file', file, file.name)
    if (userId) fd.append('userId', userId)
    return this.http.post<any>(`${environment.apiUrl}invoices/import-excel`, fd).pipe(
      map((r) => ({ error: r?.error ?? false, msg: r?.msg ?? 'Importación procesada', data: (r?.data ?? null) as ImportInvoiceResult })),
      catchError((e) => of({ error: true, msg: e?.error?.msg || e?.error?.message || 'Error al importar el Excel', data: null })),
    )
  }

  uploadFiles(files: File[], userId?: string): Observable<Wrapped<UploadInvoiceFilesResult | null>> {
    const fd = new FormData()
    files.forEach((f) => fd.append('files', f, f.name))
    if (userId) fd.append('userId', userId)
    return this.http.post<any>(`${environment.apiUrl}invoices/upload-files`, fd).pipe(
      map((r) => ({ error: r?.error ?? false, msg: r?.msg ?? 'Carga procesada', data: (r?.data ?? null) as UploadInvoiceFilesResult })),
      catchError((e) => of({ error: true, msg: e?.error?.msg || e?.error?.message || 'Error al subir los archivos', data: null })),
    )
  }

  // Carga unitaria de PDF/XML para un periodo (crea la factura de renta y la liga).
  uploadForPeriod(periodId: string, leaseId: string, files: File[], userId?: string): Observable<Wrapped<any>> {
    const fd = new FormData()
    fd.append('leaseId', leaseId)
    if (userId) fd.append('userId', userId)
    files.forEach((f) => fd.append('files', f, f.name))
    return this.http.post<any>(`${environment.apiUrl}invoices/period/${periodId}/upload`, fd).pipe(
      map((r) => ({ error: r?.error ?? false, msg: r?.msg ?? 'Factura cargada', data: r?.data ?? null })),
      catchError((e) => of({ error: true, msg: e?.error?.msg || 'Error al cargar la factura', data: null })),
    )
  }

  // Carga unitaria de PDF/XML para completar una factura PENDING (liga los archivos por Folio Fiscal).
  uploadForInvoice(invoiceId: string, files: File[], userId?: string): Observable<Wrapped<any>> {
    const fd = new FormData()
    if (userId) fd.append('userId', userId)
    files.forEach((f) => fd.append('files', f, f.name))
    return this.http.post<any>(`${environment.apiUrl}invoices/${invoiceId}/upload`, fd).pipe(
      map((r) => ({ error: r?.error ?? false, msg: r?.msg ?? 'Factura completada', data: r?.data ?? null })),
      catchError((e) => of({ error: true, msg: e?.error?.msg || 'Error al cargar la factura', data: null })),
    )
  }

  refacturar(invoiceId: string, payload: { newFolioFiscal?: string; newUid?: string; reason?: string; userId?: string }, files?: File[]): Observable<Wrapped<any>> {
    const fd = new FormData()
    // Folio Fiscal (Serie+Folio) de la nueva factura: requerido si no se adjuntan archivos.
    if (payload.newFolioFiscal) fd.append('newFolioFiscal', payload.newFolioFiscal)
    if (payload.newUid) fd.append('newUid', payload.newUid)
    if (payload.reason) fd.append('reason', payload.reason)
    if (payload.userId) fd.append('userId', payload.userId)
    ;(files || []).forEach((f) => fd.append('files', f, f.name))
    return this.http.post<any>(`${environment.apiUrl}invoices/${invoiceId}/refacturar`, fd).pipe(
      map((r) => ({ error: r?.error ?? false, msg: r?.msg ?? 'Refacturación exitosa', data: r?.data ?? null })),
      catchError((e) => of({ error: true, msg: e?.error?.msg || 'Error al refacturar', data: null })),
    )
  }

  // Corrige la referencia de una factura «Sin archivo» (folio, arrendamiento, tipo, mes, categoría).
  // El actor sale de la sesión en el backend: no se manda `userId`.
  updateReference(
    invoiceId: string,
    patch: { folioFiscal?: string; lease?: string; concept?: string; month?: string | null; expenseCategory?: string | null; reason?: string },
  ): Observable<Wrapped<any>> {
    return this.http.patch<any>(`${environment.apiUrl}invoices/${invoiceId}/reference`, patch).pipe(
      map((r) => ({ error: r?.error ?? false, msg: r?.msg ?? 'Referencia corregida', data: r?.data ?? null })),
      catchError((e) => of({ error: true, msg: e?.error?.msg || e?.error?.message || 'No se pudo corregir la referencia', data: null })),
    )
  }

  cancel(invoiceId: string, payload: { reason: string; userId?: string }): Observable<Wrapped<any>> {
    return this.http.patch<any>(`${environment.apiUrl}invoices/${invoiceId}/cancel`, payload).pipe(
      map((r) => ({ error: r?.error ?? false, msg: r?.msg ?? 'Factura cancelada', data: r?.data ?? null })),
      catchError((e) => of({ error: true, msg: e?.error?.msg || 'Error al cancelar la factura', data: null })),
    )
  }

  getByLease(leaseId: string): Observable<Wrapped<any[]>> {
    return this.http.get<any>(`${environment.apiUrl}invoices/lease/${leaseId}`).pipe(
      map((r) => ({ error: r?.error ?? false, msg: r?.msg ?? '', data: (r?.data ?? []) as any[] })),
      catchError((e) => of({ error: true, msg: e?.error?.msg || 'Error al consultar facturas', data: [] })),
    )
  }

  getPeriodHistory(periodId: string): Observable<Wrapped<any[]>> {
    return this.http.get<any>(`${environment.apiUrl}invoices/period/${periodId}`).pipe(
      map((r) => ({ error: r?.error ?? false, msg: r?.msg ?? '', data: (r?.data ?? []) as any[] })),
      catchError((e) => of({ error: true, msg: e?.error?.msg || 'Error al consultar el historial', data: [] })),
    )
  }

  // Historial/cadena de una factura (mismo destino o sustitución) — para el modal de Historial del dashboard.
  getInvoiceHistory(invoiceId: string): Observable<Wrapped<any[]>> {
    return this.http.get<any>(`${environment.apiUrl}invoices/${invoiceId}/history`).pipe(
      map((r) => ({ error: r?.error ?? false, msg: r?.msg ?? '', data: (r?.data ?? []) as any[] })),
      catchError((e) => of({ error: true, msg: e?.error?.msg || 'Error al consultar el historial', data: [] })),
    )
  }

  getByPayment(paymentId: string): Observable<Wrapped<any[]>> {
    return this.http.get<any>(`${environment.apiUrl}invoices/payment/${paymentId}`).pipe(
      map((r) => ({ error: r?.error ?? false, msg: r?.msg ?? '', data: (r?.data ?? []) as any[] })),
      catchError((e) => of({ error: true, msg: e?.error?.msg || 'Error al consultar facturas del pago', data: [] })),
    )
  }

  // Descarga del PDF/XML (proxy del blob privado en el backend). Devuelve la respuesta
  // completa para poder leer el filename del Content-Disposition.
  downloadFile(invoiceId: string, kind: 'pdf' | 'xml'): Observable<HttpResponse<Blob>> {
    return this.http.get(`${environment.apiUrl}invoices/${invoiceId}/file/${kind}`, { responseType: 'blob', observe: 'response' })
  }

  // ── Reporte mensual descargable (.xlsx) ──
  downloadMonthlyReport(month?: string): Observable<HttpResponse<Blob>> {
    const query = month ? `?month=${encodeURIComponent(month)}` : ''
    return this.http.get(`${environment.apiUrl}invoices/report/monthly${query}`, { responseType: 'blob', observe: 'response' })
  }

  // ── Complementos de pago (REP) ──
  getComplements(invoiceId: string): Observable<Wrapped<any[]>> {
    return this.http.get<any>(`${environment.apiUrl}invoices/${invoiceId}/complements`).pipe(
      map((r) => ({ error: r?.error ?? false, msg: r?.msg ?? '', data: (r?.data ?? []) as any[] })),
      catchError((e) => of({ error: true, msg: e?.error?.msg || 'Error al consultar los complementos', data: [] })),
    )
  }

  // Carga masiva de complementos de pago (REP): agrupa PDF/XML y liga por UUID de las facturas.
  uploadComplements(files: File[], userId?: string): Observable<Wrapped<any>> {
    const fd = new FormData()
    files.forEach((f) => fd.append('files', f, f.name))
    if (userId) fd.append('userId', userId)
    return this.http.post<any>(`${environment.apiUrl}invoices/complements/upload`, fd).pipe(
      map((r) => ({ error: r?.error ?? false, msg: r?.msg ?? 'Complementos procesados', data: r?.data ?? null })),
      catchError((e) => of({ error: true, msg: e?.error?.msg || e?.error?.message || 'Error al subir los complementos', data: null })),
    )
  }

  // Descarga del PDF/XML de un complemento (proxy del blob privado).
  downloadComplementFile(complementId: string, kind: 'pdf' | 'xml'): Observable<HttpResponse<Blob>> {
    return this.http.get(`${environment.apiUrl}invoices/complements/${complementId}/file/${kind}`, { responseType: 'blob', observe: 'response' })
  }
}
